import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, Phone, ShoppingBag, X } from "lucide-react";
import { Logo } from "./Logo";
import { useCart } from "../../lib/cart";
import { EBAY, PHONE } from "../../lib/api";

const LINKS = [
  { to: "/shop", label: "Shop" },
  { to: "/#categories", label: "Categories" },
  { to: "/#fitting", label: "Fitting" },
  { to: "/#trade", label: "Trade" },
  { to: "/#faq", label: "FAQ" },
  { to: "/#contact", label: "Contact" },
];

export const Header = () => {
  const { count, setOpen } = useCart();
  const [menu, setMenu] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => {
    setMenu(false);
  }, [loc.pathname, loc.hash]);

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color] duration-300 ${
        scrolled || menu ? "border-b border-white/10 bg-[#090D12]/80 backdrop-blur-xl" : "border-b border-transparent"
      }`}
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-8 lg:px-12">
        <Logo />
        <nav className="hidden items-center gap-7 xl:flex">
          {LINKS.map((l) => (
            <NavLink
              key={l.label}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase()}-link`}
              className="font-mono text-xs uppercase tracking-[0.2em] text-slate-300 transition-colors hover:text-white"
            >
              {l.label}
            </NavLink>
          ))}
          <a
            href={EBAY}
            target="_blank"
            rel="noreferrer"
            data-testid="nav-ebay-link"
            className="font-mono text-xs uppercase tracking-[0.2em] text-[#F59E0B] transition-colors hover:text-white"
          >
            eBay shop ↗
          </a>
        </nav>
        <div className="flex items-center gap-2">
          <a
            href={`tel:${PHONE}`}
            data-testid="header-phone-link"
            className="hidden items-center gap-2 font-mono text-xs tracking-widest text-slate-300 hover:text-white md:flex"
          >
            <Phone className="h-4 w-4 text-[#E11D48]" /> {PHONE}
          </a>
          <button
            data-testid="header-cart-button"
            onClick={() => setOpen(true)}
            className="relative ml-2 flex h-10 items-center gap-2 rounded-full bg-white px-4 text-sm font-semibold text-[#090D12] transition-transform hover:scale-[1.04] active:scale-95"
          >
            <ShoppingBag className="h-4 w-4" />
            <span data-testid="header-cart-count">{count}</span>
          </button>
          <button
            data-testid="header-menu-button"
            onClick={() => setMenu((m) => !m)}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 xl:hidden"
            aria-label="Menu"
          >
            {menu ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
        </div>
      </div>
      {menu && (
        <div data-testid="mobile-menu" className="border-t border-white/10 px-4 pb-6 pt-2 xl:hidden">
          {LINKS.map((l) => (
            <Link
              key={l.label}
              to={l.to}
              data-testid={`mobile-nav-${l.label.toLowerCase()}-link`}
              className="block border-b border-white/5 py-4 font-display text-3xl font-bold uppercase"
            >
              {l.label}
            </Link>
          ))}
          <a href={EBAY} target="_blank" rel="noreferrer" data-testid="mobile-nav-ebay-link" className="block border-b border-white/5 py-4 font-display text-3xl font-bold uppercase text-[#F59E0B]">
            eBay shop ↗
          </a>
          <a href={`tel:${PHONE}`} className="mt-4 flex items-center gap-2 font-mono text-sm text-slate-300">
            <Phone className="h-4 w-4 text-[#E11D48]" /> {PHONE}
          </a>
        </div>
      )}
    </header>
  );
};
